import { Notification } from '@/components/Notification';
import { useNotification } from '@/hooks/useNotification';
import { useOrg } from '@/organization/useOrg';
import {
  useGetSubproject,
  useUpdateSubprojectDetails,
} from '@/queries/useSubprojects.ts';
import {
  alpha,
  Box,
  InputAdornment,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { useNavigate, useParams } from 'react-router-dom';
import { TransitionGroup } from 'react-transition-group';

import { IconWrapper } from '../../components/icon/IconWrapper.tsx';
import {
  BuildingAlternative,
  OnSiteCostsTechnology,
  SubprojectBuildingDetails,
  SubprojectTechnology,
} from '../../stores/SubprojectStore.ts';
import { BaseDecarbFooterBar } from '../components/BaseDecarbFooterBar.tsx';
import { usePencilsDown } from '../hooks/usePencilsDown.ts';
import { EnergyKind } from '../types';
import { syncBuildingParamsAcrossAlternatives } from '../utils.ts';
import { SpaceUsageCard } from './SpaceUsageCard.tsx';
import { SpaceUtilization } from './SpaceUtilization.tsx';

// Helper function to create a default BuildingAlternative
const createDefaultBuildingAlternative = (): BuildingAlternative => ({
  buildingParams: {
    enabled: true,
    conditionedSpace: { value: 0, unit: 'SquareMeters' },
    perviousArea: { value: 0, unit: 'SquareMeters' },
    propertySize: { value: 0, unit: 'SquareMeters' },
    buildingFootprint: { value: 0, unit: 'SquareMeters' },
    numberOfFloors: 0,
    numberOfOccupants: 0,
  },
  technologyParams: {} as Record<string, SubprojectTechnology>,
  onSiteEnergy: {
    params: [],
  },
  onSiteCosts: {
    capitalCost: 0,
    maintenanceCost: 0,
    regionalMultiplier: 0,
    majorMaintenanceCost: 0,
    technology: {} as Record<string, OnSiteCostsTechnology>,
  },
  externalEnergy: {
    params: [],
  },
  costs: {
    capitalCost: 0,
    maintenanceCost: 0,
    replacementCosts: 0,
  },
  energyDemand: {} as Record<EnergyKind, number>,
  energyOnsiteSupply: {} as Record<EnergyKind, number>,
  energyExternalDemand: {} as Record<EnergyKind, number>,
  externalEnergyCosts: {},
  tonnesCo2: 0,
  demoFudgeCosts: {
    capitalCost: 0,
    maintenanceCost: 0,
    replacementCosts: 0,
  },
  demoFudgeEnergyDemand: {} as Record<EnergyKind, number>,
});

export const SpaceUsage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { subId } = useParams<{ subId: string }>();
  const { selectedOrg } = useOrg();
  const [subproject, setSubproject] = useState<SubprojectBuildingDetails>();
  const pencilsDown = usePencilsDown();
  const { notification, showError, showSuccess, hideNotification } =
    useNotification();
  const { data: fetchedSubproject } = useGetSubproject(
    selectedOrg?.id ?? '',
    subId || '',
  );
  const updateSubproject = useUpdateSubprojectDetails(
    selectedOrg?.id ?? '',
    subId || '',
  );
  // form object using FieldValues (Record<string, any>) from react-hook-form
  const form: UseFormReturn<FieldValues> = useForm();
  const pageWidth = '80vw';

  useEffect(() => {
    if (!fetchedSubproject) {
      return;
    }

    // Default newOrRenewal to 'new' if not set
    if (!fetchedSubproject.newOrRenewal) {
      fetchedSubproject.newOrRenewal = 'new';
    }

    setSubproject(fetchedSubproject);
  }, [fetchedSubproject]);

  const saveSubprojectState = () => {
    if (!subproject) {
      return null;
    }

    // Sync building params across all three alternatives before saving
    // This ensures conventional, reduced carbon, and zero carbon all have identical building measurements
    const updatedSubproject = syncBuildingParamsAcrossAlternatives(subproject);

    return updatedSubproject;
  };

  const handleNavigationClick = (destination: string) => {
    if (pencilsDown) {
      navigate(`../${destination}`);
      return;
    }

    const updatedSubproject = saveSubprojectState();
    if (updatedSubproject === null) {
      navigate(`../${destination}`);
      return;
    }

    updateSubproject.mutate(updatedSubproject, {
      onSuccess: () => {
        showSuccess('Space usage saved successfully');
        navigate(`../${destination}`);
      },
      onError: (error) => {
        showError(`Failed to save: ${error.message}`);
      },
    });
  };

  const handleBackClick = () => {
    handleNavigationClick('building-purpose');
  };

  const handleReturnToHubClick = () => {
    // navigate to hub
    handleNavigationClick('');
  };

  const handleNextClick = () => {
    handleNavigationClick('');
  };

  if (!selectedOrg) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant={'h5'}>
          Please select an organization from the user menu to access this
          project.
        </Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'left',
            alignItems: 'center',
          }}
        >
          <Typography
            variant={'h3'}
            className={'main-header'}
            sx={{ m: 3, fontSize: 32, textAlign: 'center' }}
          >
            Space Usage
          </Typography>
        </Box>
        {!subproject && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: pageWidth,
              mt: 2,
            }}
          ></Box>
        )}
        {subproject && (
          <Paper elevation={2} sx={{ m: 2, p: [0, 2, 2, 2], width: pageWidth }}>
            <Box
              component={TransitionGroup}
              sx={{
                flexGrow: 1,
                pt: 4,
                pb: 4,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography
                variant={'h5'}
                textTransform={'uppercase'}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  letterSpacing: 8,
                }}
              >
                Building Details
              </Typography>
              <Typography
                variant={'caption'}
                sx={{
                  m: 2,
                  fontSize: 10,
                  fontWeight: 500,
                  letterSpacing: 4,
                  textTransform: 'uppercase',
                }}
              >
                Enter building-level cost, size, and lifecycle information.{' '}
                <br />
              </Typography>
              <Typography
                variant={'caption'}
                sx={{
                  mt: -2,
                  fontSize: 10,
                  fontWeight: 500,
                  letterSpacing: 4,
                  textTransform: 'uppercase',
                }}
              >
                If a section is not relevant, select{' '}
                <strong>&quot;Not applicable&quot;</strong>
              </Typography>
            </Box>
            {/* Max-width wrapper keeps toggle + cards aligned and centered */}
            <Box
              sx={{
                maxWidth: 4 * 250 + 3 * 40,
                mx: 'auto',
                px: 10,
                pb: 10,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  px: 5.5,
                  pb: 5,
                }}
              >
                <ToggleButtonGroup
                  value={subproject.newOrRenewal ?? 'new'}
                  exclusive
                  disabled={pencilsDown}
                  onChange={(_, newValue) => {
                    if (newValue !== null) {
                      const updated = structuredClone(subproject);
                      updated.newOrRenewal = newValue as 'new' | 'renewal';
                      setSubproject(updated);
                    }
                  }}
                  size={'large'}
                >
                  <ToggleButton
                    value='new'
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: 16,
                      fontWeight: 600,
                      backgroundColor:
                        (subproject.newOrRenewal ?? 'new') === 'new'
                          ? 'secondary.main'
                          : 'background.card',
                      color:
                        (subproject.newOrRenewal ?? 'new') === 'new'
                          ? 'info.dark'
                          : 'secondary.main',
                      '&:hover': {
                        backgroundColor:
                          (subproject.newOrRenewal ?? 'new') === 'new'
                            ? alpha(theme.palette.secondary.main, 0.9)
                            : alpha(theme.palette.background.card!, 0.8),
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'secondary.main',
                        color: 'info.dark',
                        '&:hover': {
                          backgroundColor: alpha(
                            theme.palette.secondary.main,
                            0.9,
                          ),
                        },
                      },
                    }}
                  >
                    New
                  </ToggleButton>
                  <ToggleButton
                    value='renewal'
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: 16,
                      fontWeight: 600,
                      backgroundColor:
                        subproject.newOrRenewal === 'renewal'
                          ? 'secondary.main'
                          : 'background.card',
                      color:
                        subproject.newOrRenewal === 'renewal'
                          ? 'info.dark'
                          : 'secondary.main',
                      '&:hover': {
                        backgroundColor:
                          subproject.newOrRenewal === 'renewal'
                            ? alpha(theme.palette.secondary.main, 0.9)
                            : alpha(theme.palette.background.card!, 0.8),
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'secondary.main',
                        color: 'info.dark',
                        '&:hover': {
                          backgroundColor: alpha(
                            theme.palette.secondary.main,
                            0.9,
                          ),
                        },
                      },
                    }}
                  >
                    Renewal
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  px: 10,
                  gap: 5,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  '& > :not(style)': {
                    width: 180,
                    flexDirection: 'column',
                    justifyContent: 'center',
                    textAlign: 'center',
                  },
                }}
              >
                <SpaceUtilization
                  keyId={'conventional'}
                  readOnly={pencilsDown}
                  form={form} // Passed to SpaceUtilization
                  save={() => {
                    if (subproject) {
                      const updatedSp = structuredClone(subproject);
                      setSubproject(updatedSp);
                    }
                  }}
                  // set conventionalAlternative now, reduced and net zero on page save
                  buildingParams={(() => {
                    // Ensure conventionalAlternative exists
                    if (!subproject.conventionalAlternative) {
                      subproject.conventionalAlternative =
                        createDefaultBuildingAlternative();
                    }
                    if (!subproject.conventionalAlternative.buildingParams) {
                      subproject.conventionalAlternative.buildingParams = {
                        enabled: true,
                        conditionedSpace: {
                          value: 0,
                          unit: 'SquareMeters',
                        },
                        perviousArea: { value: 0, unit: 'SquareMeters' },
                        propertySize: { value: 0, unit: 'SquareMeters' },
                        buildingFootprint: {
                          value: 0,
                          unit: 'SquareMeters',
                        },
                        numberOfFloors: 0,
                        numberOfOccupants: 0,
                      };
                    }
                    return subproject.conventionalAlternative.buildingParams;
                  })()}
                />
                <SpaceUsageCard>
                  <IconWrapper
                    icon={'Monitoring'}
                    sx={{ height: 86, width: 86 }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      width: '100%',
                      mt: 1,
                    }}
                  >
                    <TextField
                      label='Design Life'
                      variant='standard'
                      type='number'
                      disabled={pencilsDown}
                      value={subproject.designLifeYears ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        const updated = structuredClone(subproject);
                        updated.designLifeYears =
                          val === '' ? null : parseFloat(val);
                        setSubproject(updated);
                      }}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position='end'>
                              years
                            </InputAdornment>
                          ),
                        },
                        inputLabel: { shrink: true },
                      }}
                      sx={{ flexGrow: 1 }}
                    />
                  </Box>
                </SpaceUsageCard>
                <SpaceUsageCard>
                  <IconWrapper
                    icon={'Monitoring'}
                    sx={{ height: 86, width: 86 }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      width: '100%',
                      mt: 1,
                    }}
                  >
                    <TextField
                      label='Maintenance Cycle'
                      variant='standard'
                      type='number'
                      disabled={pencilsDown}
                      value={subproject.maintenanceCycle ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        const updated = structuredClone(subproject);
                        updated.maintenanceCycle =
                          val === '' ? null : parseFloat(val);
                        setSubproject(updated);
                      }}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position='end'>
                              years
                            </InputAdornment>
                          ),
                        },
                        inputLabel: { shrink: true },
                      }}
                      sx={{ flexGrow: 1 }}
                    />
                  </Box>
                </SpaceUsageCard>
                <SpaceUsageCard>
                  <IconWrapper
                    icon={'AnnualCost'}
                    sx={{ height: 86, width: 86 }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      width: '100%',
                    }}
                  >
                    <NumericFormat
                      customInput={TextField}
                      thousandSeparator={true}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      autoComplete='off'
                      label='Design Cost'
                      helperText='Used to estimate passive system costs (15% of this amount).'
                      variant='standard'
                      disabled={pencilsDown}
                      value={subproject.designCost ?? ''}
                      onValueChange={(values: any) => {
                        const { value } = values;
                        const updated = structuredClone(subproject);
                        updated.designCost =
                          value === '' ? null : parseFloat(value);
                        setSubproject(updated);
                      }}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position='start'>$</InputAdornment>
                          ),
                        },
                      }}
                      sx={{ flexGrow: 1 }}
                    />
                  </Box>
                </SpaceUsageCard>
                <SpaceUsageCard>
                  <IconWrapper
                    icon={'AnnualCost'}
                    sx={{ height: 86, width: 86 }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      width: '100%',
                    }}
                  >
                    <NumericFormat
                      customInput={TextField}
                      thousandSeparator={true}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      autoComplete='off'
                      label='Construction Cost'
                      variant='standard'
                      disabled={pencilsDown}
                      value={subproject.constructionCost ?? ''}
                      onValueChange={(values: any) => {
                        const { value } = values;
                        const updated = structuredClone(subproject);
                        updated.constructionCost =
                          value === '' ? null : parseFloat(value);
                        setSubproject(updated);
                      }}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position='start'>$</InputAdornment>
                          ),
                        },
                      }}
                      sx={{ flexGrow: 1 }}
                    />
                  </Box>
                </SpaceUsageCard>
                <SpaceUsageCard>
                  <IconWrapper
                    icon={'AnnualCost'}
                    sx={{ height: 86, width: 86 }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      width: '100%',
                    }}
                  >
                    <NumericFormat
                      customInput={TextField}
                      thousandSeparator={true}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      autoComplete='off'
                      label='Major Maintenance Cost'
                      variant='standard'
                      disabled={pencilsDown}
                      value={subproject.majorMaintenanceCost ?? ''}
                      onValueChange={(values: any) => {
                        const { value } = values;
                        const updated = structuredClone(subproject);
                        updated.majorMaintenanceCost =
                          value === '' ? null : parseFloat(value);
                        setSubproject(updated);
                      }}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position='start'>$</InputAdornment>
                          ),
                        },
                      }}
                      sx={{ flexGrow: 1 }}
                    />
                  </Box>
                </SpaceUsageCard>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
      <BaseDecarbFooterBar
        onBackClick={handleBackClick}
        onNextClick={handleNextClick}
        onReturnToHubClick={handleReturnToHubClick}
        returnText={
          pencilsDown
            ? 'RETURN TO DECARBONIZATION HUB'
            : 'SAVE & RETURN TO DECARBONIZATION HUB'
        }
      />
      <Notification notification={notification} onClose={hideNotification} />
    </React.Fragment>
  );
};
